import type { Task } from '../../../../../types';
import type { InlineRequirement } from '../../types';
import type { MantineWizardFormData } from '../mantineFormSchema';

/**
 * Transform existing Task to Mantine form data for editing
 *
 * Converts a Task object to the flat MantineWizardFormData structure
 * used by the Mantine form hook. This is the new preferred method
 * for populating the edit form.
 *
 * @param task - The Task object to transform
 * @param formatDateForInput - Function to format Date to locale-specific input string
 * @returns Partial form data with all task fields populated
 */
export const transformTaskToMantineFormData = (
  task: Task,
  formatDateForInput: (date: Date | string | null | undefined) => string
): Partial<MantineWizardFormData> => {
  // Transform requirements to inline requirements
  const inlineRequirements: InlineRequirement[] = task.requirements.map(
    (req) => ({
      id: req.id,
      text: req.text,
      completed: req.completed,
      order: req.order,
    })
  );

  // Note: anchorPoint is managed by Zustand (tasks.formAnchorPoint), not Mantine form
  // It's set separately in useTaskWizardLogic.ts via tasks.setFormAnchorPoint(task.anchorPoint)
  return {
    // Step 1 fields (flattened from nested structure)
    title: task.title,
    priority: task.priority,
    assignedToId: task.assignedToId ?? '',
    startDate: formatDateForInput(task.startDate),
    dueDate: formatDateForInput(task.dueDate),
    location: task.location || '',
    locationFromMap: false, // Cannot determine from saved task

    // Step 2 fields
    description: task.description,
    attachments: [], // Files can't be reconstructed from attachments

    // Step 3 fields
    requirements: inlineRequirements,

    // Step 4 fields
    relatedTaskIds:
      task.relatedTasks?.map((relatedTask) => relatedTask.id) || [],
    requireRelatedTasksCompletion: task.requireRelatedTasksCompletion || false,

    // Meta
    currentStep: 1, // Always start at step 1 when editing
  };
};
